import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Tag, MessageSquare, Settings, UserCircle2,
  LogOut, Search, Beer, Menu, X, Utensils, ClipboardList, BarChart3, LayoutGrid, ChefHat, Plus,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ErrorBoundary from "../ErrorBoundary";

// ── Order alerts (module-level singleton) ────────────────────────────────────
// Lives OUTSIDE React so the watcher survives navigation: AppLayout remounts on every
// route, but the poll (Commit 2: WebSocket), the pending-order state and (Commit 3) the
// alarm persist. The shell (nav badge) and the Orders board both subscribe via
// useOrderAlerts(); the board also drives optimistic updates through orderAlerts.mutate.
// It only runs for a venue with ordering enabled — otherwise a no-op: no poll, no audio,
// no console noise. Everything goes through start/stop/refetch so Commit 2 can swap the
// poll for a WebSocket (and later a Redis fan-out) without touching call sites.

const ORDER_POLL_MS = 10000;       // fallback poll cadence (only while the WebSocket is down)
const ORDER_WS_MAX_BACKOFF = 30000;
const ORDER_WS_PING_MS = 25000;    // client → server heartbeat while the socket is open
const ORDER_WS_STALE_MS = 60000;   // no inbound frame for this long ⇒ treat the socket as dead
// The board list mirrors GET /orders' server filter, so a pushed order is merged in or
// dropped by exactly the same rule (live status + not awaiting online payment).
const ORDER_LIVE_STATUSES = ["pending", "preparing", "ready"];

// checkRev ticks on every dine-in check.* event on the shared /ws/orders socket, so screens that
// render check state (the Tables floor) can refetch live off the SAME subscription — no new poll (H-5).
let _oaState = { orders: [], pendingCount: 0, loading: true, lastFetch: null, connected: false, started: false, alarmActive: false, checkRev: 0 };
let _oaVenueKey = null;
let _oaPollTimer = null;
let _oaSeenPending = null;          // Set of known pending order ids; null until the first load
let _oaNavigate = null;             // set by AppLayout so alerts use SPA navigation
let _oaWs = null;                   // current WebSocket, or null
let _oaWsReconnectTimer = null;
let _oaWsBackoff = 1000;            // reconnect backoff, grows to ORDER_WS_MAX_BACKOFF
let _oaWantWs = false;              // true while the watcher is started (drives reconnects)
let _oaAudioCtx = null;             // lazily created; resumed on a user gesture (autoplay policy)
let _oaAlarmTimer = null;           // the looping-beep interval, or null when silent
let _oaPingTimer = null;            // heartbeat sender, or null
let _oaWatchdogTimer = null;        // staleness checker, or null
let _oaLastRx = 0;                  // timestamp of the last inbound frame of any kind
const _oaListeners = new Set();

function _oaEmit() { for (const cb of _oaListeners) cb(); }
function _oaSet(patch) {
  _oaState = { ..._oaState, ...patch };
  if ("orders" in patch) {
    _oaState.pendingCount = (patch.orders || []).filter(o => o.status === "pending").length;
  }
  _oaEmit();
}

function _oaGoToOrders() {
  // SPA navigation when we have it (toast / notification click); full load as a fallback.
  if (_oaNavigate) { try { _oaNavigate("/orders"); return; } catch (_) {} }
  try { window.location.assign("/orders"); } catch (_) {}
}

// Fire-and-forget alert for newly-arrived orders. NEVER awaited by the fetch path — a
// failed toast must never stop orders from rendering. Commit 3 adds the looping alarm and
// browser notifications on top of this.
function _oaAlertNew(newOrders) {
  try {
    const n = newOrders.length;
    const first = newOrders[0];
    toast.success(n === 1 ? `New order #${first.order_number}` : `${n} new orders`, {
      duration: 6000,
      action: { label: "View", onClick: _oaGoToOrders },
    });
  } catch (_) { /* alerts are best-effort */ }
}

// ── Looping alarm (Commit 3) ──
// A genuinely-audible tone looped until acknowledged. Two purposes: it alerts staff, and —
// per Chrome — an audible tab is exempt from background-timer throttling, so looping while
// an order waits keeps detection instant even behind a POS window. The AudioContext starts
// suspended (autoplay policy) and is resumed on a user gesture (unlockAudio); resume() is
// ALWAYS fire-and-forget — never awaited — so audio can never block anything (the old hang).
function _oaEnsureAudio() {
  if (!_oaAudioCtx) {
    try { _oaAudioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch (_) { _oaAudioCtx = null; }
  }
  if (_oaAudioCtx && _oaAudioCtx.state === "suspended") {
    try { _oaAudioCtx.resume().catch(() => {}); } catch (_) {}
  }
  return _oaAudioCtx;
}
function _oaBeep() {
  const c = _oaAudioCtx;
  if (!c || c.state !== "running") return;   // only when actually running — never blocks
  try {
    const now = c.currentTime;
    const o1 = c.createOscillator(); const g1 = c.createGain();
    o1.connect(g1); g1.connect(c.destination);
    o1.type = "sine"; o1.frequency.value = 880;
    g1.gain.setValueAtTime(0, now);
    g1.gain.linearRampToValueAtTime(0.5, now + 0.05);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
    o1.start(now); o1.stop(now + 0.32);
    const o2 = c.createOscillator(); const g2 = c.createGain();
    o2.connect(g2); g2.connect(c.destination);
    o2.type = "sine"; o2.frequency.value = 1108;
    g2.gain.setValueAtTime(0, now + 0.24);
    g2.gain.linearRampToValueAtTime(0.42, now + 0.3);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.62);
    o2.start(now + 0.24); o2.stop(now + 0.62);
  } catch (_) {}
}
function _oaStartAlarm() {
  if (_oaAlarmTimer) return;                  // already ringing
  _oaSet({ alarmActive: true });
  _oaEnsureAudio();
  _oaBeep();
  // Loop until acknowledged (Zomato-style) — this is also what keeps a backgrounded tab
  // unthrottled while the order waits.
  _oaAlarmTimer = setInterval(() => { _oaEnsureAudio(); _oaBeep(); }, 1800);
}
function _oaStopAlarm() {
  if (_oaAlarmTimer) { clearInterval(_oaAlarmTimer); _oaAlarmTimer = null; }
  if (_oaState.alarmActive) _oaSet({ alarmActive: false });
}
// Silence once nothing is pending (all orders accepted) so it doesn't ring forever after
// staff clear the board without hitting Acknowledge.
function _oaReconcileAlarm() {
  if (_oaState.pendingCount === 0) _oaStopAlarm();
}

// Browser notification so an alert surfaces OVER the POS / Zomato window. Only when the tab
// is hidden (a visible tab already has the toast + alarm) and permission is granted; degrades
// silently otherwise. Clicking focuses the tab and opens the board.
function _oaNotify(newOrders) {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (!document.hidden) return;
    const n = newOrders.length;
    const title = n === 1 ? `New order #${newOrders[0].order_number}` : `${n} new orders`;
    const notif = new Notification(title, { body: "Tap to open the orders board", tag: "tabon-order", renotify: true });
    notif.onclick = () => { try { window.focus(); } catch (_) {} _oaGoToOrders(); try { notif.close(); } catch (_) {} };
  } catch (_) { /* notifications are best-effort */ }
}

// Seed on first load, then raise a fire-and-forget alert for any newly-arrived pending
// order. Shared by the fetch path and the WebSocket-merge path so both alert identically.
function _oaDetectAndAlert(orders) {
  const pendingIds = orders.filter(o => o.status === "pending").map(o => o.id);
  if (_oaSeenPending === null) { _oaSeenPending = new Set(pendingIds); return; }   // seed, don't alert
  const fresh = orders.filter(o => o.status === "pending" && !_oaSeenPending.has(o.id));
  _oaSeenPending = new Set(pendingIds);
  if (fresh.length) {
    // All three are fire-and-forget: toast, looping alarm, and (backgrounded) notification.
    _oaAlertNew(fresh);
    _oaStartAlarm();
    _oaNotify(fresh);
  }
}

// Full list fetch — the initial populate, the WS-open resync, and the polling fallback all
// use this. It never touches audio, so it can never block the board's first render (the
// old hang lived in the chime, not here).
async function _oaFetchOnce() {
  try {
    const { data } = await api.get("/orders");
    const orders = Array.isArray(data) ? data : [];
    _oaDetectAndAlert(orders);
    _oaSet({ orders, loading: false, lastFetch: new Date() });
    _oaReconcileAlarm();
  } catch (e) {
    if (e?.response?.status === 401) { _oaStop(); return; }   // logout / expiry → stop, no 401 spam
    _oaSet({ loading: false });                               // keep last orders on a transient error
  }
}

// Merge one pushed order into the shared list, mirroring GET /orders' server filter: keep
// it while it's a live order not awaiting an online payment; drop it once it leaves that set
// (collected / cancelled / delivered / paying-online). No second fetch needed.
function _oaShouldShow(o) {
  return ORDER_LIVE_STATUSES.includes(o.status) && o.payment_status !== "pending";
}
function _oaMerge(order) {
  if (!order || !order.id) return;
  const list = _oaState.orders;
  const present = list.some(o => o.id === order.id);
  let next;
  if (_oaShouldShow(order)) {
    next = present ? list.map(o => (o.id === order.id ? order : o)) : [...list, order];
  } else {
    if (!present) return;                        // not shown and not present — nothing to do
    next = list.filter(o => o.id !== order.id);
  }
  _oaDetectAndAlert(next);
  _oaSet({ orders: next, lastFetch: new Date() });
  _oaReconcileAlarm();
}

function _oaWsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return base.replace(/^http/, "ws") + "/api/ws/orders";     // http→ws, https→wss
}

function _oaStopPoll() {
  if (_oaPollTimer) { clearInterval(_oaPollTimer); _oaPollTimer = null; }
}
// Polling fallback — runs ONLY while the socket is down (a proxy that blocks upgrades, or a
// mid-session drop), so a venue always gets orders, just slower.
function _oaStartPollFallback() {
  if (_oaPollTimer) return;
  _oaFetchOnce();
  _oaPollTimer = setInterval(_oaFetchOnce, ORDER_POLL_MS);
}

function _oaScheduleReconnect() {
  if (_oaWsReconnectTimer || !_oaWantWs) return;
  // ±50% jitter so every client doesn't retry in lockstep after a deploy / backend restart
  // (thundering herd).
  const delay = Math.round(_oaWsBackoff * (0.5 + Math.random()));
  _oaWsBackoff = Math.min(_oaWsBackoff * 2, ORDER_WS_MAX_BACKOFF);
  _oaWsReconnectTimer = setTimeout(() => { _oaWsReconnectTimer = null; _oaConnectWs(); }, delay);
}

function _oaStopHeartbeat() {
  if (_oaPingTimer) { clearInterval(_oaPingTimer); _oaPingTimer = null; }
  if (_oaWatchdogTimer) { clearInterval(_oaWatchdogTimer); _oaWatchdogTimer = null; }
}

// A WebSocket often dies WITHOUT firing onclose (wifi drop, NAT/router idle timeout, a
// sleeping tablet), and since onopen stopped the poll the board would look connected yet
// receive nothing. So we treat SILENCE as the failure signal: ping the server, and if no
// inbound frame of any kind (pong, order event, anything) arrives for ORDER_WS_STALE_MS,
// force the socket closed and recover through the normal fallback + reconnect path.
function _oaStartHeartbeat() {
  _oaStopHeartbeat();
  _oaLastRx = Date.now();
  _oaPingTimer = setInterval(() => {
    try {
      if (_oaWs && _oaWs.readyState === 1) _oaWs.send(JSON.stringify({ type: "ping" }));
    } catch (_) {}
  }, ORDER_WS_PING_MS);
  _oaWatchdogTimer = setInterval(() => {
    if (Date.now() - _oaLastRx <= ORDER_WS_STALE_MS) return;
    // Stale: drop the (silently dead) socket ourselves and recover, since onclose won't.
    const ws = _oaWs;
    _oaWs = null;
    _oaStopHeartbeat();
    _oaSet({ connected: false });
    try { if (ws) { ws.onclose = null; ws.close(); } } catch (_) {}
    if (_oaWantWs) { _oaStartPollFallback(); _oaScheduleReconnect(); }
  }, 5000);
}

// A tablet waking from sleep may have missed events on a socket that looks open — resync
// immediately when the tab becomes visible rather than waiting for the next event.
function _oaOnVisible() {
  if (!document.hidden && _oaWantWs) _oaFetchOnce();
}

function _oaConnectWs() {
  if (!_oaWantWs) return;
  let ws;
  try {
    ws = new WebSocket(_oaWsUrl());
  } catch (_) {
    _oaStartPollFallback(); _oaScheduleReconnect(); return;
  }
  _oaWs = ws;
  ws.onopen = () => {
    _oaWsBackoff = 1000;
    _oaStopPoll();                 // WebSocket is the transport now
    _oaStartHeartbeat();           // ping + staleness watchdog
    _oaFetchOnce();               // resync — the socket only carries events from here on
    _oaSet({ connected: true });
  };
  ws.onmessage = (ev) => {
    _oaLastRx = Date.now();        // ANY inbound frame proves the socket is alive (feeds the watchdog)
    try {
      const msg = JSON.parse(ev.data);
      if (!msg || msg.type === "pong") return;   // heartbeat reply — nothing more to do
      if (msg.order) _oaMerge(msg.order);
      // A dine-in check changed (round added, kitchen status, bill printed, settled, …): tick
      // checkRev so the Tables floor refetches off this SAME socket (H-5). Orders don't need it.
      if (msg.check || (typeof msg.type === "string" && msg.type.startsWith("check."))) {
        _oaSet({ checkRev: (_oaState.checkRev || 0) + 1 });
      }
    } catch (_) { /* ignore malformed frames */ }
  };
  ws.onclose = () => {
    _oaWs = null;
    _oaStopHeartbeat();
    _oaSet({ connected: false });
    if (_oaWantWs) { _oaStartPollFallback(); _oaScheduleReconnect(); }   // keep orders flowing + retry
  };
  ws.onerror = () => { try { ws.close(); } catch (_) {} };               // → onclose → fallback + reconnect
}

function _oaStop() {
  _oaWantWs = false;
  if (_oaWsReconnectTimer) { clearTimeout(_oaWsReconnectTimer); _oaWsReconnectTimer = null; }
  _oaStopHeartbeat();
  try { document.removeEventListener("visibilitychange", _oaOnVisible); } catch (_) {}
  if (_oaWs) { try { _oaWs.onclose = null; _oaWs.close(); } catch (_) {} _oaWs = null; }   // null onclose → no reconnect
  _oaStopPoll();
  if (_oaAlarmTimer) { clearInterval(_oaAlarmTimer); _oaAlarmTimer = null; }
  _oaVenueKey = null;
  _oaSeenPending = null;
  _oaState = { orders: [], pendingCount: 0, loading: true, lastFetch: null, connected: false, started: false, alarmActive: false, checkRev: 0 };
  _oaEmit();
}

function _oaStart(venueKey) {
  if (_oaState.started && _oaVenueKey === venueKey) return;   // already running for this venue
  if (_oaState.started) _oaStop();                            // a different venue logged in → reset
  _oaVenueKey = venueKey;
  _oaSeenPending = null;
  _oaWantWs = true;
  _oaWsBackoff = 1000;
  _oaState = { ..._oaState, started: true, loading: true };
  _oaEmit();
  try { document.addEventListener("visibilitychange", _oaOnVisible); } catch (_) {}
  _oaFetchOnce();       // instant populate (works before the socket opens / if it never does)
  _oaConnectWs();       // then realtime via WebSocket; polling fallback kicks in on failure
}

// Stable public surface — module-level, so the board can call these without re-subscribing.
const orderAlerts = {
  subscribe(cb) { _oaListeners.add(cb); return () => { _oaListeners.delete(cb); }; },
  getSnapshot() { return _oaState; },
  ensureStarted(venueKey) { if (venueKey) _oaStart(venueKey); },
  stop() { _oaStop(); },
  refetch() { return _oaFetchOnce(); },
  setNavigate(fn) { _oaNavigate = fn; },
  acknowledge() { _oaStopAlarm(); },
  // Resume the audio context on a user gesture so the alarm can actually sound (autoplay
  // policy). Idempotent and never throws.
  unlockAudio() { _oaEnsureAudio(); },
  // Ask for notification permission once, from a deliberate ordering context (never on first
  // page load). Best-effort — silent if unsupported or denied.
  requestNotifyPermission() {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch (_) {}
  },
  // Optimistic local update from the board (status change / removal). Accepts an array or
  // an updater fn (mirrors setState) and updates the shared snapshot + the nav badge.
  mutate(next) {
    const orders = typeof next === "function" ? next(_oaState.orders) : next;
    _oaSet({ orders });
    _oaReconcileAlarm();
  },
};

// Subscribe a component to the shared order state; re-renders on every change.
export function useOrderAlerts() {
  const [snap, setSnap] = React.useState(orderAlerts.getSnapshot);
  React.useEffect(() => orderAlerts.subscribe(() => setSnap(orderAlerts.getSnapshot())), []);
  return snap;
}

export { orderAlerts };

const ALL_ADMIN_LINKS = [
  { to: "/dashboard",   label: "Dashboard",       icon: LayoutDashboard, feature: null },
  { to: "/orders",      label: "Orders",          icon: ClipboardList,   feature: "ordering" },
  { to: "/tables",      label: "Tables",          icon: LayoutGrid,      feature: "dine_in" },
  { to: "/kitchen",     label: "Kitchen",         icon: ChefHat,         feature: "kitchen_board" },
  { to: "/reports",     label: "Reports",         icon: BarChart3,       feature: "ordering_or_loyalty" },
  { to: "/billing",     label: "Billing Counter", icon: Beer,            feature: "loyalty_or_bottle" },
  { to: "/customers",   label: "Customers",       icon: Users,           feature: "loyalty_or_bottle" },
  { to: "/menu",        label: "Menu",            icon: Utensils,        feature: "qr_menu" },
  { to: "/promotions",  label: "Campaigns",       icon: Tag,             feature: "campaigns" },
  { to: "/whatsapp-log",label: "WhatsApp Log",    icon: MessageSquare,   feature: "any_whatsapp" },
  { to: "/settings",    label: "Settings",        icon: Settings,        feature: null },
];

const ALL_STAFF_LINKS = [
  { to: "/orders",  label: "Orders",          icon: ClipboardList, feature: "ordering" },
  // Floor staff run the Tables floor and the Kitchen board (Prompt U, Commit 1) — gated by the same
  // feature rules as the admin nav (dine_in needs ordering + dine-in mode; kitchen needs the toggle).
  { to: "/tables",  label: "Tables",          icon: LayoutGrid,    feature: "dine_in" },
  { to: "/kitchen", label: "Kitchen",         icon: ChefHat,       feature: "kitchen_board" },
  { to: "/billing", label: "Billing Counter", icon: Beer,          feature: null },
];

export function AppLayout({ children }) {
  const { user, logout, hasFeature } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Shell-level order watcher: runs on EVERY screen (not just the board), so staff on
  // Billing Counter still see incoming orders. Gated on ordering; the pending count feeds
  // the nav badge below. The singleton persists across navigation — starting is idempotent
  // and we never stop on unmount (it stops itself on logout / 401).
  const ordering = hasFeature("ordering");
  const venueKey = user?.venue?.id || user?.venue?.venue_code || user?.venue_id || null;
  const { pendingCount, alarmActive } = useOrderAlerts();
  React.useEffect(() => {
    orderAlerts.setNavigate(navigate);
    if (ordering && venueKey) orderAlerts.ensureStarted(venueKey);
  }, [ordering, venueKey, navigate]);

  // Resume the alarm's AudioContext on the first user gesture (autoplay policy) so it can
  // actually sound when an order arrives — including while the tab is later backgrounded.
  React.useEffect(() => {
    if (!ordering) return;
    const unlock = () => orderAlerts.unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [ordering]);

  const isAdmin = user?.role === "admin";
  const allLinks = isAdmin ? ALL_ADMIN_LINKS : ALL_STAFF_LINKS;

  // WhatsApp-failures-today badge on the WhatsApp Log nav item (I-5b), so a silent template failure
  // is visible without opening the log. Admin-only (the health endpoint is), refreshed every 5 min.
  const [waFailedToday, setWaFailedToday] = React.useState(0);
  React.useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const load = () => api.get("/whatsapp-log/health")
      .then(({ data }) => { if (!cancelled) setWaFailedToday(Number(data?.failed_today) || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin]);
  const links = allLinks.filter(l => {
    if (!l.feature) return true;
    if (l.feature === "any_whatsapp") {
      return hasFeature("loyalty_points") || hasFeature("bottle_locker") ||
             hasFeature("campaigns") || hasFeature("stamp_cards");
    }
    if (l.feature === "loyalty_or_bottle") {
      return hasFeature("loyalty_points") || hasFeature("bottle_locker");
    }
    if (l.feature === "ordering_or_loyalty") {
      return hasFeature("ordering") || hasFeature("loyalty_points");
    }
    // Tables is dine-in only — shown when ordering is on AND the venue takes dine-in orders.
    if (l.feature === "dine_in") {
      return hasFeature("ordering") && ["dine_in", "both"].includes(user?.venue?.ordering_mode);
    }
    // Kitchen board: dine-in AND behind the kitchen_board_enabled venue toggle (default off, G-6).
    if (l.feature === "kitchen_board") {
      return hasFeature("ordering") && ["dine_in", "both"].includes(user?.venue?.ordering_mode)
             && !!user?.venue?.kitchen_board_enabled;
    }
    return hasFeature(l.feature);
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      {/* ---- NEW-ORDER ALARM BAR (every page) ---- */}
      {/* Shown while the looping alarm is active; Acknowledge silences it (Zomato-style),
          tapping the bar opens the board. Available on every screen so staff on Billing can
          silence without navigating. Fixed to the bottom so it never clashes with the sticky
          header or the fixed orders board. */}
      {alarmActive && (
        <div
          data-testid="order-alarm-banner"
          onClick={() => navigate("/orders")}
          className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between gap-3 px-4 py-3 cursor-pointer shadow-2xl"
          style={{ background: "#9D00FF", color: "#fff" }}
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            <ClipboardList size={16} />
            New order{pendingCount > 1 ? `s (${pendingCount})` : ""} — tap to open
          </span>
          <button
            type="button"
            data-testid="order-alarm-ack"
            onClick={(e) => { e.stopPropagation(); orderAlerts.acknowledge(); }}
            className="shrink-0 px-4 py-1.5 rounded-lg text-xs font-bold bg-white text-[#9D00FF] hover:bg-white/90"
          >
            Acknowledge
          </button>
        </div>
      )}

      {/* ---- TOP NAV (desktop + tablet) ---- */}
      <header className="hidden md:flex items-center justify-between border-b border-white/5 bg-[#0A0A0A]/95 backdrop-blur px-6 py-3 sticky top-0 z-40">
        <div className="flex items-center gap-8">
          <Link to={isAdmin ? "/dashboard" : (hasFeature("ordering") ? "/orders" : "/billing")} data-testid="nav-logo-link">
            <Logo size="md" />
          </Link>
          <nav className="flex items-center gap-1">
            {links.map((l) => {
              const Icon = l.icon;
              const active = location.pathname.startsWith(l.to);
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.to.replace("/", "")}`}
                  className={`relative flex items-center gap-2 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-[#9D00FF]/15 text-white border border-[#9D00FF]/40"
                      : "text-[#A0A0A0] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={16} />
                  {l.label}
                  {l.to === "/orders" && pendingCount > 0 && (
                    <span
                      data-testid="orders-pending-badge"
                      className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold bg-[#9D00FF] text-white"
                    >
                      {pendingCount > 99 ? "99+" : pendingCount}
                    </span>
                  )}
                  {l.to === "/whatsapp-log" && waFailedToday > 0 && (
                    <span
                      data-testid="wa-failed-badge"
                      title={`${waFailedToday} message${waFailedToday > 1 ? "s" : ""} didn't reach a customer today`}
                      className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold bg-red-500 text-white"
                    >
                      {waFailedToday > 99 ? "99+" : waFailedToday}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {/* New Order from anywhere (Prompt S, Commit 5): navigates to the board and opens the
              modal via ?new=1. The modal is NOT lifted into the layout — it needs the venue, the
              menu and somewhere to send the result, and must refresh the page that placed it. */}
          {hasFeature("ordering") && (
            <button
              type="button"
              onClick={() => navigate("/orders?new=1")}
              data-testid="chrome-new-order"
              className="flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] rounded-lg bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-sm font-semibold transition"
            >
              <Plus size={15} /> New Order
            </button>
          )}
          <div className="hidden lg:flex items-center gap-2 text-xs">
            <div className="flex flex-col items-end">
              <span className="text-white font-semibold">{user?.name}</span>
              <span className="text-[#A0A0A0]">
                {user?.venue?.name}
                {user?.role === "admin" && (
                  <span className="ml-2 font-mono text-[#9D00FF]">#{user?.venue?.venue_code}</span>
                )}
              </span>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#2D1B4E] flex items-center justify-center border border-[#9D00FF]/30">
              <UserCircle2 size={18} className="text-[#9D00FF]" />
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 px-3 py-2 min-h-[44px] rounded-lg text-sm text-[#A0A0A0] hover:text-white hover:bg-white/5 transition"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      {/* ---- MOBILE TOP BAR ---- */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#0A0A0A]/95 backdrop-blur sticky top-0 z-40">
        <Logo size="sm" />
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-button"
          className="p-2 rounded-lg border border-white/10 text-white"
        >
          <Menu size={20} />
        </button>
      </header>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden bg-black/70 backdrop-blur-sm">
          <div className="absolute right-0 top-0 h-full w-72 bg-[#1A1A1A] border-l border-white/10 p-5 flex flex-col gap-2">
            <div className="flex items-center justify-between mb-4">
              <Logo size="sm" />
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg border border-white/10"
                data-testid="mobile-close-button"
              >
                <X size={18} />
              </button>
            </div>
            <div className="text-xs text-[#A0A0A0] mb-2">
              {user?.name} · {user?.venue?.name}
            </div>
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <button
                  key={l.to}
                  onClick={() => {
                    navigate(l.to);
                    setMobileOpen(false);
                  }}
                  data-testid={`mobile-link-${l.to.replace("/", "")}`}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-left text-white hover:bg-white/5 border border-transparent hover:border-[#9D00FF]/30"
                >
                  <Icon size={18} className="text-[#9D00FF]" />
                  {l.label}
                  {l.to === "/orders" && pendingCount > 0 && (
                    <span
                      data-testid="orders-pending-badge-mobile"
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-[#9D00FF] text-white"
                    >
                      {pendingCount > 99 ? "99+" : pendingCount}
                    </span>
                  )}
                  {l.to === "/whatsapp-log" && waFailedToday > 0 && (
                    <span
                      data-testid="wa-failed-badge-mobile"
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-red-500 text-white"
                    >
                      {waFailedToday > 99 ? "99+" : waFailedToday}
                    </span>
                  )}
                </button>
              );
            })}
            <button
              onClick={logout}
              className="mt-auto flex items-center gap-3 px-3 py-3 rounded-lg text-left text-[#EF4444] hover:bg-[#EF4444]/10 border border-[#EF4444]/30"
              data-testid="mobile-logout"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      )}

      {/* A render crash in the page content shows the fallback here, inside the shell, so the nav
          above stays usable (AI-4). Keyed by route so navigating to another screen clears the error
          and the new page renders instead of staying stuck on the fallback. */}
      <main className="flex-1 pb-24 md:pb-8">
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </main>

      {/* ---- MOBILE BOTTOM NAV (staff quick actions) ---- */}
      <BottomNav />

      <footer className="hidden md:block text-center text-xs text-[#A0A0A0] py-4 border-t border-white/5">
        Powered by <span className="text-white">TabOn</span> · tabon.app
      </footer>
    </div>
  );
}

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeature } = useAuth();
  const items = [
    ...(hasFeature("loyalty_points") ? [{ to: "/billing?tab=search", label: "Search", icon: Search, match: /billing/ }] : []),
    ...(hasFeature("bottle_locker") ? [{ to: "/billing?tab=tabs", label: "Active Tab", icon: Beer, match: /billing/ }] : []),
  ];
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10 flex items-stretch">
      {items.map((it) => {
        const Icon = it.icon;
        const active = it.match.test(location.pathname);
        return (
          <button
            key={it.label}
            data-testid={`bottom-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => navigate(it.to)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 ${
              active ? "text-[#9D00FF]" : "text-[#A0A0A0]"
            }`}
          >
            <Icon size={20} />
            <span className="text-[11px] font-semibold">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
